import React, { useState, useMemo } from "react";
import { Clock, Pencil, Trash2, Calendar, ChevronLeft, ChevronRight, User, MapPin } from "lucide-react";
import { isPastSchedule } from "../../validator/workScheduleValidator";

export default function ScheduleList({
    schedules = [],
    loading,
    error,
    onEdit,
    onDelete,
    formatDate,
    selectedDate,
    pagination,
    onPageChange,
    canEdit,
    canDelete,
}) {
    const [showPastSchedules, setShowPastSchedules] = useState(false);

    const getFieldValue = (schedule, field, fallback = "N/A") => {
        const possibleFields = {
            date: ["ngay_tiep_dan", "date", "ngayTiepDan"],
            time: ["thoi_gian", "time", "thoiGian"],
            leader: ["ten_can_bo", "leader", "tenCanBo", "can_bo"],
            location: ["dia_diem", "location", "diaDiem"],
            purpose: ["ghi_chu", "purpose", "ghiChu"],
        };

        if (possibleFields[field]) {
            for (const fieldName of possibleFields[field]) {
                if (schedule[fieldName]) {
                    return schedule[fieldName];
                }
            }
        }

        return schedule[field] || fallback;
    };

    const getStartTimeFromSchedule = (schedule) => {
        const timeValue = getFieldValue(schedule, "time");
        if (timeValue && typeof timeValue === "string") {
            const timeParts = timeValue.split(" - ");
            if (timeParts.length >= 1) {
                return timeParts[0].trim();
            }
        }
        return null;
    };

    const isSchedulePassed = (schedule) => {
        const scheduleDate = getFieldValue(schedule, "date");
        const startTime = getStartTimeFromSchedule(schedule);
        if (typeof isPastSchedule === "function") {
            return isPastSchedule(scheduleDate, startTime);
        }
        if (!scheduleDate) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const scheduleDateObj = new Date(scheduleDate);
        scheduleDateObj.setHours(0, 0, 0, 0);
        return scheduleDateObj < today;
    };

    const displayedSchedules = useMemo(() => {
        if (!Array.isArray(schedules)) return [];
        if (selectedDate) return schedules;
        if (showPastSchedules) return schedules;
        return schedules.filter((s) => !isSchedulePassed(s));
    }, [schedules, selectedDate, showPastSchedules]);

    const getDisplayMessage = () => {
        if (selectedDate) {
            const selectedSchedules = schedules.filter(
                (s) => getFieldValue(s, "date") === selectedDate
            );
            if (selectedSchedules.length === 0) {
                const formattedDate = formatDate
                    ? formatDate(selectedDate)
                    : selectedDate;
                return {
                    icon: <Calendar className="w-12 h-12 text-gray-300 mx-auto" />,
                    title: "Không có lịch tiếp dân",
                    message: `Ngày ${formattedDate} chưa có lịch tiếp dân nào`,
                };
            }
        }

        if (!showPastSchedules && schedules.length > 0) {
            return {
                icon: <Calendar className="w-12 h-12 text-gray-300 mx-auto" />,
                title: "Không có lịch tiếp dân sắp tới",
                message: "Tất cả các lịch trong danh sách đã qua. Tích chọn \"Hiển thị lịch đã qua\" để xem lại các lịch cũ.",
            };
        }

        return {
            icon: <Calendar className="w-12 h-12 text-gray-300 mx-auto" />,
            title: "Chưa có lịch tiếp dân",
            message: "Hãy thêm lịch mới hoặc import từ file Excel",
        };
    };

    const displayMessage = getDisplayMessage();

    const renderPagination = () => {
        if (!pagination || selectedDate) return null;

        const { currentPage, totalPages, pageSize, totalItems } = pagination;

        if (totalPages <= 1) return null;

        const pages = [];
        const maxPages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxPages / 2));
        let endPage = Math.min(totalPages, startPage + maxPages - 1);

        if (endPage - startPage < maxPages - 1) {
            startPage = Math.max(1, endPage - maxPages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return (
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200 pt-4 gap-3">
                <div className="text-xs sm:text-sm text-gray-600">
                    Hiển thị {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalItems)} trong tổng số {totalItems} lịch
                </div>
                <div className="flex items-center gap-1.5">
                    <button
                        onClick={() => onPageChange?.(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>

                    {startPage > 1 && (
                        <>
                            <button
                                onClick={() => onPageChange?.(1)}
                                className="px-2.5 py-1 rounded border border-gray-300 hover:bg-gray-50 text-xs"
                            >
                                1
                            </button>
                            {startPage > 2 && <span className="text-gray-500 text-xs">...</span>}
                        </>
                    )}

                    {pages.map(page => (
                        <button
                            key={page}
                            onClick={() => onPageChange?.(page)}
                            className={`px-2.5 py-1 rounded text-xs border ${page === currentPage
                                    ? 'bg-blue-600 text-white border-blue-600 font-semibold'
                                    : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                                }`}
                        >
                            {page}
                        </button>
                    ))}

                    {endPage < totalPages && (
                        <>
                            {endPage < totalPages - 1 && <span className="text-gray-500 text-xs">...</span>}
                            <button
                                onClick={() => onPageChange?.(totalPages)}
                                className="px-2.5 py-1 rounded border border-gray-300 hover:bg-gray-50 text-xs"
                            >
                                {totalPages}
                            </button>
                        </>
                    )}

                    <button
                        onClick={() => onPageChange?.(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="p-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="bg-white rounded-xl shadow-xs border border-gray-200 p-4 md:p-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                    <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-0">
                        {selectedDate
                            ? "Lịch tiếp dân trong ngày"
                            : showPastSchedules
                            ? "Tất cả lịch tiếp dân"
                            : "Lịch tiếp dân sắp tới"}
                    </h2>
                    <span className="text-xs md:text-sm font-medium text-gray-500">
                        ({displayedSchedules.length} lịch)
                    </span>
                </div>

                {!selectedDate && (
                    <label className="inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-gray-700 cursor-pointer select-none bg-gray-50 hover:bg-gray-100 border border-gray-200 px-3 py-1.5 rounded-lg transition-colors">
                        <input
                            type="checkbox"
                            checked={showPastSchedules}
                            onChange={(e) => setShowPastSchedules(e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                        />
                        <span>Hiển thị lịch đã qua</span>
                    </label>
                )}
            </div>

            {loading ? (
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            ) : displayedSchedules.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                    <div className="mb-3 text-4xl">{displayMessage.icon}</div>
                    <p className="text-base font-medium mb-1">{displayMessage.title}</p>
                    <p className="text-xs text-gray-400">{displayMessage.message}</p>
                </div>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-2xs">
                    <table className="w-full min-w-[720px] text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50/80 border-b border-gray-200 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                <th className="py-3 px-3.5 text-center w-12">#</th>
                                <th className="py-3 px-4 whitespace-nowrap">Ngày tiếp dân</th>
                                <th className="py-3 px-4 whitespace-nowrap">Khung giờ</th>
                                <th className="py-3 px-4 whitespace-nowrap">Cán bộ tiếp dân</th>
                                <th className="py-3 px-4 whitespace-nowrap">Địa điểm</th>
                                <th className="py-3 px-4 whitespace-nowrap">Ghi chú</th>
                                <th className="py-3 px-4 text-center whitespace-nowrap w-28">Thao tác</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 text-xs sm:text-sm bg-white">
                            {displayedSchedules.map((schedule, index) => {
                                const displayIndex = selectedDate
                                    ? index + 1
                                    : ((pagination?.currentPage || 1) - 1) * (pagination?.pageSize || 10) + index + 1;
                                const isPassed = isSchedulePassed(schedule);
                                const dateVal = getFieldValue(schedule, "date");
                                const timeVal = getFieldValue(schedule, "time");
                                const leaderVal = getFieldValue(schedule, "leader");
                                const locationVal = getFieldValue(schedule, "location");
                                const purposeVal = getFieldValue(schedule, "purpose", "");

                                return (
                                    <tr
                                        key={schedule.id}
                                        className={`hover:bg-blue-50/40 transition-colors ${
                                            isPassed ? "opacity-70 bg-gray-50/50" : ""
                                        }`}
                                    >
                                        {/* # STT */}
                                        <td className="py-3 px-3.5 text-center text-xs font-medium text-gray-500 whitespace-nowrap">
                                            {displayIndex}
                                        </td>

                                        {/* Ngày tiếp dân */}
                                        <td className="py-3 px-4 whitespace-nowrap">
                                            <span
                                                className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold ${
                                                    isPassed
                                                        ? "bg-gray-100 text-gray-600 border border-gray-200"
                                                        : "bg-blue-50 text-blue-600 border border-blue-200/80"
                                                }`}
                                            >
                                                {formatDate ? formatDate(dateVal) : dateVal}
                                            </span>
                                        </td>

                                        {/* Khung giờ */}
                                        <td className="py-3 px-4 whitespace-nowrap">
                                            <div className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700">
                                                <Clock className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                                                <span>{timeVal}</span>
                                            </div>
                                        </td>

                                        {/* Cán bộ tiếp dân */}
                                        <td className="py-3 px-4 whitespace-nowrap">
                                            {leaderVal && leaderVal !== "N/A" ? (
                                                <div className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-800">
                                                    <User className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                                    <span>{leaderVal}</span>
                                                </div>
                                            ) : (
                                                <span className="text-xs text-gray-400 italic">---</span>
                                            )}
                                        </td>

                                        {/* Địa điểm */}
                                        <td className="py-3 px-4 whitespace-nowrap">
                                            {locationVal && locationVal !== "N/A" ? (
                                                <div className="inline-flex items-center gap-1.5 text-xs text-gray-700">
                                                    <MapPin className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                                                    <span>{locationVal}</span>
                                                </div>
                                            ) : (
                                                <span className="text-xs text-gray-400 italic">---</span>
                                            )}
                                        </td>

                                        {/* Ghi chú */}
                                        <td className="py-3 px-4 max-w-[200px]">
                                            <span className="text-xs text-gray-600 truncate block" title={purposeVal && purposeVal !== "N/A" ? purposeVal : "---"}>
                                                {purposeVal && purposeVal !== "N/A" ? purposeVal : <span className="text-gray-400 italic">---</span>}
                                            </span>
                                        </td>

                                        {/* Thao tác */}
                                        <td className="py-3 px-4 text-center whitespace-nowrap">
                                            <div className="flex items-center justify-center gap-1">
                                                {onEdit && !isPassed && (!canEdit || canEdit(schedule)) && (
                                                    <button
                                                        onClick={() => onEdit(schedule)}
                                                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                                                        title="Chỉnh sửa lịch"
                                                    >
                                                        <Pencil className="w-4 h-4" />
                                                    </button>
                                                )}
                                                {onDelete && !isPassed && (!canDelete || canDelete(schedule)) && (
                                                    <button
                                                        onClick={() => onDelete(schedule)}
                                                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                                                        title="Xóa lịch"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                                {(!onEdit || isPassed || (canEdit && !canEdit(schedule))) &&
                                                    (!onDelete || isPassed || (canDelete && !canDelete(schedule))) && (
                                                        <span className="text-xs text-gray-400">—</span>
                                                    )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {renderPagination()}
        </div>
    );
}
